import {
  ArrowForwardRounded,
  BookmarkBorderRounded,
  CheckCircleOutlineRounded,
  CheckCircleRounded,
  SearchRounded,
  WorkOutlineRounded
} from "@mui/icons-material";
import { Box, Button, Card, CardContent, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { PORTALS } from "../content";
import { PageContainer, SectionHeading, sectionSx } from "./SectionLayout";

function LegacyPortalCard({
  accent,
  background,
  description,
  features,
  icon: Icon,
  id,
  onClick,
  subtitle,
  title
}) {
  return (
    <Card
      id={id}
      elevation={0}
      sx={{
        bgcolor: background,
        border: "1px solid",
        borderColor: alpha(accent, 0.2),
        borderRadius: 2.5,
        height: "100%",
        scrollMarginTop: 95,
        transition: "border-color 180ms ease, transform 180ms ease",
        "&:hover": { borderColor: alpha(accent, 0.52), transform: "translateY(-3px)" }
      }}
    >
      <CardContent sx={{ p: { xs: 3, md: 4 }, "&:last-child": { pb: { xs: 3, md: 4 } } }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              alignItems: "center",
              bgcolor: accent,
              borderRadius: 1.5,
              color: "common.white",
              display: "flex",
              height: 50,
              justifyContent: "center",
              width: 50
            }}
          >
            <Icon />
          </Box>
          <Box>
            <Typography component="h3" sx={{ fontSize: 21, fontWeight: 800 }}>
              {title}
            </Typography>
            <Typography color="text.secondary">{subtitle}</Typography>
          </Box>
        </Stack>
        <Typography color="text.secondary" sx={{ fontSize: 15, lineHeight: 1.7, mt: 3 }}>
          {description}
        </Typography>
        <Stack spacing={1.5} my={3.5}>
          {features.map((feature) => (
            <Stack key={feature} direction="row" spacing={1.25} alignItems="center">
              <CheckCircleRounded sx={{ color: accent, fontSize: 19 }} />
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{feature}</Typography>
            </Stack>
          ))}
        </Stack>
        <Button
          onClick={onClick}
          endIcon={<ArrowForwardRounded />}
          sx={{ color: accent, px: 0, "&:hover": { bgcolor: "transparent", gap: 0.5 } }}
        >
          Explore {title.replace("For ", "")} Portal
        </Button>
      </CardContent>
    </Card>
  );
}

export function LegacyAudienceCards({ onFindJobs, onStartHiring }) {
  return (
    <Box
      sx={{
        display: "grid",
        gap: 3,
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }
      }}
    >
      {PORTALS.map((portal) => (
        <LegacyPortalCard
          key={portal.id}
          {...portal}
          onClick={portal.action === "employer" ? onStartHiring : onFindJobs}
        />
      ))}
    </Box>
  );
}

function FeaturePills({ accent, compact = false, features }) {
  return (
    <Box
      sx={{
        display: compact ? { xs: "flex", md: "grid" } : "flex",
        flexWrap: "wrap",
        gap: compact ? { xs: 0.8, md: 0.55, lg: 0.65 } : 0.8,
        gridTemplateColumns: compact ? "repeat(2, max-content)" : undefined,
        maxWidth: 290
      }}
    >
      {features.map((feature) => (
        <Stack
          key={feature}
          direction="row"
          spacing={0.65}
          alignItems="center"
          sx={{
            bgcolor: alpha("#ffffff", 0.88),
            border: "1px solid",
            borderColor: alpha(accent, 0.14),
            borderRadius: 0.8,
            boxShadow: `0 3px 10px ${alpha(accent, 0.06)}`,
            minHeight: compact ? { xs: 24, md: 22 } : 24,
            px: compact ? { xs: 0.75, md: 0.45, lg: 0.55 } : 0.75,
            py: 0.35
          }}
        >
          <CheckCircleOutlineRounded
            sx={{
              color: accent,
              fontSize: compact ? { xs: 12, md: 10, lg: 11 } : 12
            }}
          />
          <Typography
            component="span"
            sx={{
              color: "text.primary",
              fontSize: compact ? { xs: 8.5, md: 7.5, lg: 8 } : 8.5,
              fontWeight: 500,
              whiteSpace: "nowrap"
            }}
          >
            {feature}
          </Typography>
        </Stack>
      ))}
    </Box>
  );
}

function EmployerPreview({ image, imageAlt }) {
  return (
    <Box
      sx={{
        alignItems: "center",
        display: "flex",
        height: "100%",
        justifyContent: "center",
        minHeight: { xs: 235, sm: 330, md: 390, lg: 340 },
        overflow: "hidden",
        position: "relative"
      }}
    >
      <Box
        sx={{
          bgcolor: "common.white",
          border: "1px solid",
          borderColor: alpha("#451ebb", 0.08),
          borderRadius: 1.5,
          boxShadow: `0 16px 36px ${alpha("#2d196f", 0.13)}`,
          left: { xs: 12, sm: -25, md: -75, lg: -65 },
          overflow: "hidden",
          position: "absolute",
          top: { xs: 20, sm: "50%" },
          transform: { sm: "translateY(-50%)" },
          width: { xs: "calc(100% - 18px)", sm: 360, md: 340, lg: 400 }
        }}
      >
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ borderBottom: "1px solid", borderColor: "#eeecf7", height: 20, px: 1 }}
        >
          {["#ff8f8f", "#f6c960", "#66c998"].map((color) => (
            <Box key={color} sx={{ bgcolor: color, borderRadius: "50%", height: 4, width: 4 }} />
          ))}
          <Box sx={{ bgcolor: "#f3f1f8", borderRadius: 4, height: 7, ml: "auto", width: "38%" }} />
        </Stack>
        <Box
          component="img"
          src={image}
          alt={imageAlt}
          sx={{ display: "block", height: "auto", width: "100%" }}
        />
      </Box>

      <Box
        aria-hidden="true"
        sx={{
          alignItems: "center",
          bgcolor: alpha("#ffffff", 0.94),
          border: "1px solid",
          borderColor: alpha("#451ebb", 0.1),
          borderRadius: 1.25,
          boxShadow: `0 10px 24px ${alpha("#2d196f", 0.12)}`,
          display: { xs: "none", sm: "flex" },
          flexDirection: "column",
          gap: 0.25,
          p: 1,
          position: "absolute",
          right: { sm: 5, md: 0, lg: 4 },
          top: { sm: 74, md: 88, lg: 68 }
        }}
      >
        <Stack spacing={0.35}>
          {[12, 16, 10].map((width) => (
            <Box
              key={width}
              sx={{ bgcolor: alpha("#451ebb", 0.16), borderRadius: 4, height: 3, width }}
            />
          ))}
        </Stack>
        <Typography sx={{ color: "#451ebb", fontSize: 7.5, fontWeight: 800 }}>Top Match</Typography>
      </Box>

      <Box
        aria-hidden="true"
        sx={{
          alignItems: "center",
          bgcolor: alpha("#ffffff", 0.96),
          border: "1px solid",
          borderColor: alpha("#2da875", 0.17),
          borderRadius: "50%",
          bottom: { xs: 12, sm: 47, md: 54, lg: 42 },
          boxShadow: `0 10px 28px ${alpha("#1c6c4e", 0.15)}`,
          display: "flex",
          flexDirection: "column",
          height: { xs: 58, sm: 62 },
          justifyContent: "center",
          position: "absolute",
          right: { xs: 9, sm: 7, md: -2, lg: 0 },
          width: { xs: 58, sm: 62 }
        }}
      >
        <Typography sx={{ color: "#19825b", fontSize: 16, fontWeight: 900, lineHeight: 1 }}>
          98%
        </Typography>
        <Typography sx={{ color: "text.secondary", fontSize: 7, mt: 0.35 }}>Top Match</Typography>
      </Box>
    </Box>
  );
}

const PHONE_JOBS = [
  { color: "#7258dc", title: "UI/UX Designer", salary: "₹8–10 LPA" },
  { color: "#6247c5", title: "Software Engineer", salary: "₹10–15 LPA" },
  { color: "#dd897d", title: "Product Manager", salary: "₹20–25 LPA" }
];

function CandidatePreview() {
  return (
    <Box
      aria-label="NextHire candidate mobile job search preview"
      role="img"
      sx={{
        alignItems: "flex-end",
        display: "flex",
        height: "100%",
        justifyContent: "center",
        minHeight: { xs: 330, sm: 360, md: 420, lg: 360 },
        overflow: "hidden",
        position: "relative",
        pt: { xs: 2, sm: 3 }
      }}
    >
      <Box
        sx={{
          background: "linear-gradient(180deg, #faf9ff 0%, #ffffff 42%)",
          border: "5px solid",
          borderColor: alpha("#dfdbe9", 0.92),
          borderBottom: 0,
          borderRadius: "29px 29px 0 0",
          boxShadow: `0 14px 32px ${alpha("#2c2a45", 0.16)}`,
          height: { xs: 322, sm: 342, md: 385, lg: 342 },
          overflow: "hidden",
          position: "relative",
          width: { xs: 184, sm: 194, md: 178, lg: 202 }
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            bgcolor: "#d4d1dc",
            borderRadius: 3,
            height: 4,
            left: "50%",
            position: "absolute",
            top: 8,
            transform: "translateX(-50%)",
            width: 30,
            zIndex: 2
          }}
        />

        <Box
          sx={{
            background: "linear-gradient(140deg, #eeebff 0%, #f8f6ff 100%)",
            px: 1.4,
            pb: 1.5,
            pt: 3.2
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ color: "#242230", fontSize: 11.5, lineHeight: 1.16 }}>
              Find your
              <Box component="span" sx={{ color: "#5134c6", display: "block", fontWeight: 900 }}>
                Dream Job
              </Box>
            </Typography>
            <Box
              sx={{
                alignItems: "center",
                bgcolor: alpha("#ffffff", 0.68),
                borderRadius: "50%",
                color: "#5134c6",
                display: "flex",
                height: 23,
                justifyContent: "center",
                width: 23
              }}
            >
              <WorkOutlineRounded sx={{ fontSize: 13 }} />
            </Box>
          </Stack>
          <Stack
            direction="row"
            spacing={0.6}
            alignItems="center"
            sx={{ bgcolor: "common.white", borderRadius: 1, boxShadow: "0 2px 8px #ddd8ef", mt: 1.3, p: 0.85 }}
          >
            <SearchRounded sx={{ color: "#8a8696", fontSize: 12 }} />
            <Typography sx={{ color: "#8a8696", fontSize: 6.8 }}>Search job title, skills...</Typography>
          </Stack>
        </Box>

        <Box sx={{ px: 1.35, py: 1.15 }}>
          <Typography sx={{ color: "#35313f", fontSize: 7.5, fontWeight: 800, mb: 0.85 }}>
            Popular Jobs
          </Typography>
          <Stack spacing={0.75}>
            {PHONE_JOBS.map((job) => (
              <Stack
                key={job.title}
                direction="row"
                spacing={0.7}
                alignItems="center"
                sx={{
                  bgcolor: "common.white",
                  border: "1px solid #f0eef5",
                  borderRadius: 1,
                  boxShadow: "0 3px 9px rgba(44, 42, 69, 0.05)",
                  p: 0.75
                }}
              >
                <Box
                  sx={{
                    alignItems: "center",
                    bgcolor: alpha(job.color, 0.16),
                    borderRadius: 0.65,
                    color: job.color,
                    display: "flex",
                    height: 18,
                    justifyContent: "center",
                    width: 18
                  }}
                >
                  <WorkOutlineRounded sx={{ fontSize: 10 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ color: "#302d38", fontSize: 6.8, fontWeight: 800 }}>
                    {job.title}
                  </Typography>
                  <Typography sx={{ color: "#8d8994", fontSize: 5.4 }}>Full-time</Typography>
                </Box>
                <Typography sx={{ fontSize: 5.8, fontWeight: 800, ml: "auto !important" }}>
                  {job.salary}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>

        <Box
          aria-hidden="true"
          sx={{
            alignItems: "center",
            bgcolor: "#5134c6",
            borderRadius: "50%",
            bottom: 9,
            boxShadow: `0 5px 12px ${alpha("#5134c6", 0.35)}`,
            color: "common.white",
            display: "flex",
            height: 24,
            justifyContent: "center",
            position: "absolute",
            right: 9,
            width: 24
          }}
        >
          <BookmarkBorderRounded sx={{ fontSize: 12 }} />
        </Box>
      </Box>
    </Box>
  );
}

function ExperienceCard({
  accent,
  action,
  background,
  description,
  icon: Icon,
  id,
  onClick,
  previewAlt,
  previewImage,
  showcaseFeatures,
  subtitle,
  title
}) {
  const isEmployer = action === "employer";

  return (
    <Card
      id={id}
      elevation={0}
      sx={{
        background: `linear-gradient(135deg, ${background} 0%, ${alpha(background, 0.74)} 100%)`,
        border: "1px solid",
        borderColor: alpha(accent, 0.08),
        borderRadius: { xs: 2.5, sm: 3 },
        boxShadow: `0 14px 38px ${alpha(accent, 0.07)}`,
        minWidth: 0,
        overflow: "hidden",
        scrollMarginTop: 95,
        transition: "border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease",
        "&:hover": {
          borderColor: alpha(accent, 0.2),
          boxShadow: `0 18px 44px ${alpha(accent, 0.11)}`,
          transform: "translateY(-3px)"
        }
      }}
    >
      <CardContent
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            sm: "minmax(0, 1.18fr) minmax(210px, 0.82fr)",
            md: isEmployer
              ? "repeat(2, minmax(0, 1fr))"
              : "minmax(0, 1.28fr) minmax(145px, 0.72fr)",
            lg: isEmployer
              ? "repeat(2, minmax(0, 1fr))"
              : "minmax(0, 1.12fr) minmax(225px, 0.88fr)"
          },
          minHeight: { sm: 390, md: 440, lg: 370 },
          p: 0,
          "&:last-child": { pb: 0 }
        }}
      >
        <Box
          sx={{
            alignItems: "flex-start",
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            p: {
              xs: 3,
              sm: 3.5,
              md: isEmployer ? 2 : 3,
              lg: isEmployer ? 3 : 3.5
            },
            pb: { xs: 1.5, sm: 3.5, md: 3, lg: 3.5 },
            position: "relative",
            zIndex: 1
          }}
        >
          <Box
            sx={{
              alignItems: "center",
              bgcolor: accent,
              borderRadius: "50%",
              boxShadow: `0 7px 16px ${alpha(accent, 0.25)}`,
              color: "common.white",
              display: "flex",
              height: 38,
              justifyContent: "center",
              mb: 2,
              width: 38
            }}
          >
            <Icon sx={{ fontSize: 19 }} />
          </Box>

          <Typography
            component="h3"
            sx={{
              color: "text.primary",
              fontSize: { xs: 22, lg: 23 },
              fontWeight: 500,
              lineHeight: 1.2
            }}
          >
            {title}
          </Typography>
          <Typography sx={{ color: "text.primary", fontSize: 13.5, fontWeight: 400, mt: 0.55 }}>
            {subtitle}
          </Typography>
          <Typography
            color="text.secondary"
            sx={{
              fontSize: 11.5,
              lineHeight: 1.7,
              maxWidth: 390,
              mt: 1.15
            }}
          >
            {description}
          </Typography>

          <Box
            sx={{
              bgcolor: alpha(accent, 0.25),
              flexShrink: 0,
              height: "1px",
              my: 1.6,
              width: 90
            }}
          />
          <FeaturePills accent={accent} compact={isEmployer} features={showcaseFeatures} />

          <Button
            onClick={onClick}
            endIcon={<ArrowForwardRounded sx={{ fontSize: "18px !important" }} />}
            sx={{
              color: accent,
              fontSize: 13.5,
              fontWeight: 700,
              mt: "auto",
              pb: 0,
              pt: 2.6,
              px: 0,
              transformOrigin: "left center",
              transition: "gap 180ms ease, transform 180ms ease",
              "&:hover": {
                bgcolor: "transparent",
                gap: 0.35,
                transform: "scale(1.03)"
              }
            }}
          >
            Explore {title.replace("For ", "")} Portal
          </Button>
        </Box>

        {isEmployer ? (
          <EmployerPreview image={previewImage} imageAlt={previewAlt} />
        ) : (
          <CandidatePreview />
        )}
      </CardContent>
    </Card>
  );
}

export default function AudienceSection({ onCandidateLogin, onStartHiring }) {
  return (
    <Box component="section" sx={{ ...sectionSx, bgcolor: "background.paper" }}>
      <PageContainer>
        <SectionHeading title="Two Powerful Experiences, One Platform" />
        {/*
          Previous audience-card design kept as a rollback option.
          To restore it, comment out the ExperienceCard grid below and uncomment:
          <LegacyAudienceCards onFindJobs={onCandidateLogin} onStartHiring={onStartHiring} />
        */}
        <Box
          sx={{
            display: "grid",
            gap: { xs: 2.5, md: 2 },
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }
          }}
        >
          {PORTALS.map((portal) => (
            <ExperienceCard
              key={portal.id}
              {...portal}
              onClick={portal.action === "employer" ? onStartHiring : onCandidateLogin}
            />
          ))}
        </Box>
      </PageContainer>
    </Box>
  );
}
